import React, { useState, useRef, useEffect, useCallback } from 'react';
import './BoardComments.sass';

const MentionTextarea = ({ 
  value, 
  onChange, 
  onMentionsChange,
  placeholder = 'Add a comment...',
  disabled = false 
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [mentions, setMentions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch users matching the query
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const res = await fetch(`${base}/api/messaging/search-users?query=${encodeURIComponent(query)}`);
      const data = await res.json();
      
      if (data.ok && data.users) {
        setSuggestions(data.users.slice(0, 6));
        setShowSuggestions(data.users.length > 0);
        setSelectedSuggestionIndex(0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error('Error fetching mention suggestions:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (mentionQuery && mentionStartIndex >= 0) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(mentionQuery);
      }, 200);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [mentionQuery, mentionStartIndex, fetchSuggestions]);

  // Handle text input
  const handleInput = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(newValue);

    // Check if we're in a mention context
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex >= 0) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's no space between @ and cursor (valid mention context)
      if (!/\s/.test(textAfterAt)) {
        setMentionStartIndex(lastAtIndex);
        setMentionQuery(textAfterAt);
      } else {
        setMentionStartIndex(-1);
        setMentionQuery('');
        setShowSuggestions(false);
      }
    } else {
      setMentionStartIndex(-1);
      setMentionQuery('');
      setShowSuggestions(false);
    }
  };

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === 'Enter' && showSuggestions) {
      e.preventDefault();
      selectSuggestion(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Select a suggestion
  const selectSuggestion = (user) => {
    if (!user || mentionStartIndex < 0) return;

    const handle = user.handle || user.name;
    const beforeMention = value.substring(0, mentionStartIndex);
    const afterMention = value.substring(mentionStartIndex + 1 + mentionQuery.length);
    const newValue = `${beforeMention}@${handle} ${afterMention}`;
    
    onChange(newValue);

    // Add to mentions list if not already there
    const newMention = {
      userId: user.userId,
      handle: handle,
      userType: user.custom?.userType || user.role?.toLowerCase(),
      creatorProfileId: user.creatorProfileId,
      hostProfileId: user.hostProfileId
    };

    const updatedMentions = [...mentions];
    if (!updatedMentions.some(m => m.userId === newMention.userId)) {
      updatedMentions.push(newMention);
      setMentions(updatedMentions);
      if (onMentionsChange) {
        onMentionsChange(updatedMentions);
      }
    }

    // Reset mention state
    setMentionStartIndex(-1);
    setMentionQuery('');
    setShowSuggestions(false);
    setSuggestions([]);

    // Focus back on textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset mentions when value is cleared
  useEffect(() => {
    if (!value || value.trim() === '') {
      setMentions([]);
      if (onMentionsChange) {
        onMentionsChange([]);
      }
    }
  }, [value, onMentionsChange]);

  return (
    <div className="mention-textarea-container">
      <textarea
        ref={textareaRef}
        className="mention-textarea"
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div ref={suggestionsRef} className="mention-suggestions">
          {isLoading ? (
            <div className="mention-suggestion-loading">Loading...</div>
          ) : (
            suggestions.map((user, index) => (
              <div
                key={user.id || user.userId}
                className={`mention-suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                onClick={() => selectSuggestion(user)}
                onMouseEnter={() => setSelectedSuggestionIndex(index)}
              >
                <img 
                  src={user.image} 
                  alt={user.name} 
                  className="mention-suggestion-avatar"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random&size=32`;
                  }}
                />
                <div className="mention-suggestion-info">
                  <span className="mention-suggestion-name">{user.name}</span>
                  {user.handle && (
                    <span className="mention-suggestion-handle">@{user.handle}</span>
                  )}
                </div>
                <span className="mention-suggestion-role">{user.role}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
